import ExtensionIcon from '@mui/icons-material/Extension';
import GroupsIcon from '@mui/icons-material/Groups';
import InsightsIcon from '@mui/icons-material/Insights';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import ThreePOutlinedIcon from '@mui/icons-material/ThreePOutlined';
import { Badge, Box, Collapse, List } from '@mui/material';
import MuiButton from '@mui/material/Button';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import { useState } from 'react';
import { HiOutlineOfficeBuilding } from 'react-icons/hi';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { amplitude } from 'wisestep-api';
import {
  AnalyticsIconAlt,
  CandidateIconAlt,
  HomeIconAlt,
  JobsIconAlt,
  MailIconAlt,
  SettingsIconAlt,
  VendorsIconAlt,
  WsColors,
} from 'wisestep-base';

import { useWorkspaceContext } from '../../../src/context';
import { useClientAuthz, ClientAction } from '../../hooks/useClientAuthz';
import { CrmJobsAccess, useCrmJobsAuthz } from '../../hooks/useCrmJobsAuthz';
import { LeadAccess, useLeadAuthz } from '../../hooks/useLeadAuthz';
import { OurEntityAction, useOurEntityAuthz } from '../../hooks/useOurEntityAuthz';
import { ActivateFeature } from '../ActivateFeature';

interface MenuRoutesProps {
  path: string;
  title: string;
  icon?: JSX.Element;
  subMenu?: MenuRoutesProps[];
  feature?: string;
  badge?: boolean;
  showBetaIcon?: boolean;
}

const MenuList = ({
  open,
  toggleDrawer,
  closeDrawer,
  isAdmin,
  unReadCount,
}: {
  open: boolean;
  toggleDrawer: (val: boolean) => void;
  closeDrawer?: () => void;
  isAdmin?: boolean;
  unReadCount: number;
}) => {
  const isAuthorizedViewClientListing = useClientAuthz(ClientAction.VIEW_CLIENT_LISTING_ALL);
  const isAuthorizedViewClientUnit = useClientAuthz(ClientAction.VIEW_CLIENT_UNIT);
  const isAuthorizedViewClientContactUnit = useClientAuthz(ClientAction.VIEW_CLIENT_CONTACTS_UNIT);
  const isAuthorizedViewOurEntityList = useOurEntityAuthz(OurEntityAction.VIEW_ENTITY_LISTING_PAGE);
  const isAuthorizedViewLeadListing = useLeadAuthz(LeadAccess.VIEW_LEAD_LISTING_ALL);
  const isAuthorizedViewCrmJobsListing = useCrmJobsAuthz(CrmJobsAccess.VIEW_CRM_JOB_LISTING_ALL);
  const workspace = useWorkspaceContext();
  const currentPath = useLocation().pathname;
  const Button = styled(MuiButton)(() => ({
    borderRadius: 60,
    height: '2.5vh',
    width: '10px',
    paddingLeft: '11px',
    paddingRight: '11px',
    backgroundColor: WsColors.TheBlues900,
    fontSize: '8px',
  }));
  const routes: MenuRoutesProps[] = [
    {
      path: '/home',
      title: 'Home',
      icon: <HomeIconAlt />,
    },
    {
      path: '/jobs/',
      title: 'Jobs',
      icon: <JobsIconAlt />,
      subMenu: [
        {
          path: '/jobs/',
          title: 'Jobs List',
          icon: <JobsIconAlt />,
        },
        {
          path: '/jobs/new',
          title: 'Create New Job',
          icon: <JobsIconAlt />,
          feature: 'JS_INTERNAL_JOB_POSTING',
        },
        { path: '/consultant', title: 'Hot Jobs' },
      ],
    },
    {
      path: '/candidates/',
      title: 'Candidates',
      icon: <CandidateIconAlt />,
      subMenu: [
        {
          path: `/candidates/`,
          title: 'Talent Pool',
        },
        {
          path: '/native-search-new/dice',
          title: 'Job Boards',
        },
        { path: '/all-submissions', title: 'Submissions' },
        { path: '/active-consultants/', title: 'Active Consultants' },
        { path: '/import-candidates/', title: 'Import Candidates' },
        { path: 'native-search/sourcing-bots', title: 'Sourcing Bots' },
      ],
    },
    ...(workspace.owns ||
    isAuthorizedViewClientListing ||
    isAuthorizedViewOurEntityList ||
    isAuthorizedViewLeadListing
      ? [
          {
            path: '/clients/',
            title: 'CRM',
            icon: (
              <Box sx={{ width: 32, display: 'flex' }}>
                <HiOutlineOfficeBuilding style={{ color: '#A0A4C0', margin: 'auto' }} size={24} />
              </Box>
            ),
            subMenu: [
              ...(workspace.owns || isAuthorizedViewCrmJobsListing
                ? [{ path: '/crm-jobs/', title: 'Jobs' }]
                : []),
              ...(workspace.owns || isAuthorizedViewLeadListing
                ? [{ path: '/leads/', title: 'Leads' }]
                : []),
              ...(workspace.owns || isAuthorizedViewClientListing
                ? [{ path: '/clients/', title: 'Companies' }]
                : []),
              ...(workspace.owns || isAuthorizedViewClientUnit
                ? [{ path: '/client-units', title: 'Client Unit' }]
                : []),

              { path: '/contacts-list', title: 'Contacts' },
              ...(workspace.owns || isAuthorizedViewClientContactUnit
                ? [{ path: '/client-contacts-units', title: 'Contacts Unit' }]
                : []),
              { path: '/note-list', title: 'Notes' },
              ...(workspace.owns || isAuthorizedViewOurEntityList
                ? [
                    {
                      path: '/organisationEntity/list/',
                      title: 'Organisation Entity',
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    {
      path: '/workspace-users-and-teams',
      title: 'Dashboards',
      icon: <InsightsOutlinedIcon style={{ color: WsColors.TertiaryShades500, marginLeft: 4.5 }} />,
    },
    {
      path: '/vendors',
      title: 'Vendor List',
      icon: <VendorsIconAlt />,
    },
    {
      path: '/communication-accounts/',
      title: 'Communication Accounts',
      icon: <ThreePOutlinedIcon style={{ color: WsColors.TertiaryShades500, marginLeft: 3 }} />,
      subMenu: [
        {
          path: '/email-settings/logs/inbox/',
          title: 'Email Inbox',
          icon: (
            <Badge badgeContent={unReadCount} color="error">
              <MailIconAlt />
            </Badge>
          ),
          badge: true,
        },
        { path: '/sms-logs/', title: 'SMS Logs' },
        { path: '/call-logs/', title: 'Call Logs' },
      ],
    },
    {
      path: '/app-settings/teams-and-users',
      title: 'Settings',
      icon: <SettingsIconAlt />,
    },
    // {
    //   path: '/organisationEntity/',
    //   title: 'Organisation Entity',
    //   icon: <VendorsIconAlt />,
    // },
  ];

  const adminRoutes: MenuRoutesProps[] = [
    {
      path: '/',
      title: 'Home',
      icon: <HomeIconAlt />,
    },
    {
      path: '/workspace/',
      title: 'Workspace',
      icon: <GroupsIcon />,
      subMenu: [
        {
          path: '/workspace/listing',
          title: 'Listing',
          icon: <GroupsIcon />,
        },
        {
          path: '/user-management',
          title: 'User Management',
        },
      ],
    },
    {
      path: '/business-metrics/',
      title: 'Business Metrics',
      icon: <InsightsIcon />,
      subMenu: [
        {
          path: '/business-metrics/workspace-plan',
          title: 'Workspace Plan',
        },
        {
          path: '/business-metrics/workspace-activity',
          title: 'Workspace Activity',
        },
        {
          path: '/business-metrics/workspace-added',
          title: 'Workspaces Added',
        },
        {
          path: '/business-metrics/onbording-details/save',
          title: 'Fill Onboarding Details',
        },
        {
          path: '/business-metrics/onbording-details/get',
          title: 'Paid But Not Yet Onboarded',
        },
      ],
    },
    {
      path: '/cv-upload',
      title: 'Analytics',
      icon: <AnalyticsIconAlt />,
      subMenu: [
        {
          path: '/cv-upload',
          title: 'CV Upload - Wisestep',
          icon: <AnalyticsIconAlt />,
        },
        {
          path: '/cv-parser-status',
          title: 'CV Parser - Wisestep',
          icon: <AnalyticsIconAlt />,
        },
        {
          path: '/internal-mis',
          title: 'Accross Workspace Analytics',
          icon: <AnalyticsIconAlt />,
        },
        {
          path: '/internal-mis-workspace-level',
          title: 'Workspace Level Analytics',
          icon: <AnalyticsIconAlt />,
        },
      ],
    },
    {
      path: '/candidates/',
      title: 'Candidates',
      icon: <CandidateIconAlt />,
      subMenu: [
        {
          path: '/candidates/demerge-candidates',
          title: 'Demerge Candidates',
          icon: <CandidateIconAlt />,
        },
      ],
    },
    {
      path: '/extensions/',
      title: 'Extensions',
      icon: <ExtensionIcon fontSize="medium" sx={{ pl: 0.5 }} />,
      subMenu: [
        {
          path: '/extensions/prism-extension',
          title: 'Prism Extension',
          icon: <ExtensionIcon />,
        },
        {
          path: '/extensions/private-extension',
          title: 'Private Extension',
          icon: <ExtensionIcon />,
        },
      ],
    },
  ];

  const activeRoute = (routeName) => {
    return currentPath === routeName;
  };

  const menuAmplitudeEvent = {
    ['Talent Pool']: () =>
      amplitude('Talent Pool', {
        clickedOn: 'Talent Pool',
        workspaceName: workspace.name,
        pageName: 'Candidates',
        planType: workspace.planType,
        interfaceType: 'web',
        pageUrl: window.location.href,
      }),
    ['Pipeline']: () =>
      amplitude('Pipeline_landing', {
        clickedOn: 'Pipeline',
        workspaceName: workspace.name,
        pageName: 'Pipeline',
        planType: workspace.planType,
        interfaceType: 'web',
        pageUrl: window.location.href,
      }),
    ['Submissions']: () =>
      amplitude('Submission_click', {
        clickedOn: 'Submissions',
        workspaceName: workspace.name,
        pageName: 'Candidates',
        planType: workspace.planType,
        interfaceType: 'web',
        pageUrl: window.location.href,
      }),
    ['Notes']: () =>
      amplitude('note_menu', {
        clickedOn: 'Notes',
        workspaceName: workspace.name,
        pageName: 'Clients',
        planType: workspace.planType,
        interfaceType: 'web',
        pageUrl: window.location.href,
      }),
    ['Clients']: () =>
      amplitude('client_menu', {
        clickedOn: 'Clients',
        workspaceName: workspace.name,
        pageName: 'Clients',
        planType: workspace.planType,
        interfaceType: 'web',
        pageUrl: window.location.href,
      }),
  };

  const getFilteredMenu = () => {
    if (isAdmin) {
      return adminRoutes;
    }
    return routes;
  };
  const [selectedTitle, setSelectedTitle] = useState('');

  return (
    <>
      {getFilteredMenu().map((menuOption, index) => (
        <>
          {menuOption.subMenu ? (
            <ListItemButton
              id={menuOption.title}
              onClick={() => {
                if (selectedTitle == menuOption.title) toggleDrawer(!open);
                else toggleDrawer(true);
                setSelectedTitle(menuOption.title);
              }}
              selected={
                activeRoute(menuOption.path) ||
                menuOption.subMenu?.map((obj) => obj.path).includes(currentPath)
              }
              key={index}
            >
              <Tooltip title={menuOption.title}>
                <ListItemIcon>{menuOption.icon}</ListItemIcon>
              </Tooltip>
              <ListItemText primary={menuOption.title} />
              {menuOption?.showBetaIcon && <Button variant="contained">Beta</Button>}
            </ListItemButton>
          ) : (
            <ListItemButton
              id={menuOption.title}
              component={RouterLink}
              to={menuOption.path}
              onClick={closeDrawer}
              selected={activeRoute(menuOption.path)}
              key={index}
            >
              {menuOption.badge ? (
                <Tooltip title={menuOption.title}>
                  <ListItemIcon>{menuOption.icon}</ListItemIcon>
                </Tooltip>
              ) : (
                <Tooltip title={menuOption.title}>
                  <ListItemIcon>{menuOption.icon}</ListItemIcon>
                </Tooltip>
              )}

              <ListItemText primary={menuOption.title} />
            </ListItemButton>
          )}
          <>
            {menuOption.subMenu?.map((subMenuOption, index) => (
              <>
                {menuOption.title == selectedTitle && (
                  <Collapse in={open} timeout="auto" unmountOnExit key={index}>
                    <List component="div" disablePadding>
                      {subMenuOption.feature ? (
                        <ActivateFeature feature={subMenuOption.feature}>
                          <ListItemButton
                            id={subMenuOption.title}
                            component={RouterLink}
                            to={subMenuOption.path}
                            sx={{ pl: 11 }}
                            onClick={() => {
                              subMenuOption.title === 'New Job' &&
                                amplitude('job_create_menu', {
                                  clickedOn: 'new job',
                                  pageName: 'new jobs',
                                  pageUrl: window.location.href,
                                  workspaceName: workspace.name,
                                  planType: workspace.planType,
                                  interfaceType: 'Web',
                                });
                              toggleDrawer(false);
                            }}
                            selected={activeRoute(subMenuOption.path)}
                          >
                            <ListItemText primary={subMenuOption.title} />
                          </ListItemButton>
                        </ActivateFeature>
                      ) : (
                        <ListItemButton
                          id={subMenuOption.title}
                          component={RouterLink}
                          to={subMenuOption.path}
                          sx={{ pl: 11 }}
                          onClick={() => {
                            subMenuOption.title &&
                              menuAmplitudeEvent[subMenuOption.title] &&
                              menuAmplitudeEvent[subMenuOption.title]();

                            toggleDrawer(false);
                          }}
                          selected={activeRoute(subMenuOption.path)}
                        >
                          <ListItemText primary={subMenuOption.title} />
                        </ListItemButton>
                      )}
                    </List>
                  </Collapse>
                )}
              </>
            ))}
          </>
        </>
      ))}
    </>
  );
};

export default MenuList;
